package com.schoolconnect.app.designsystem

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.EaseInOutCubic
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.draw.scale
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import androidx.compose.ui.zIndex
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.ic_launcher
import org.jetbrains.compose.resources.painterResource
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin

@Composable
fun ApiLoadingOverlay(
    isVisible: Boolean,
    modifier: Modifier = Modifier,
    title: String = "Updating data",
    subtitle: String = "Please wait while iConnect.School finishes the request."
) {
    AnimatedVisibility(
        visible = isVisible,
        enter = fadeIn(animationSpec = tween(180, easing = FastOutSlowInEasing)),
        exit = fadeOut(animationSpec = tween(120, easing = FastOutSlowInEasing))
    ) {
        val transition = rememberInfiniteTransition(label = "api_loader")
        val ringRotation by transition.animateFloat(
            initialValue = 0f,
            targetValue = 360f,
            animationSpec = infiniteRepeatable(animation = tween(2200, easing = LinearEasing)),
            label = "ringRotation"
        )
        val pulse by transition.animateFloat(
            initialValue = 0.96f,
            targetValue = 1.04f,
            animationSpec = infiniteRepeatable(
                animation = tween(1400, easing = EaseInOutCubic),
                repeatMode = RepeatMode.Reverse
            ),
            label = "pulse"
        )
        val orbit by transition.animateFloat(
            initialValue = 0f,
            targetValue = 1f,
            animationSpec = infiniteRepeatable(animation = tween(1600, easing = LinearEasing)),
            label = "orbit"
        )

        Box(
            modifier = modifier
                .fillMaxSize()
                .zIndex(10f)
                .background(MaterialTheme.colorScheme.surface.copy(alpha = 0.72f))
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = {}
                ),
            contentAlignment = Alignment.Center
        ) {
            Surface(
                modifier = Modifier.widthIn(max = 280.dp),
                shape = RoundedCornerShape(16.dp),
                color = MaterialTheme.colorScheme.surface.copy(alpha = 0.97f),
                tonalElevation = 2.dp,
                shadowElevation = 8.dp
            ) {
                Column(
                    modifier = Modifier.padding(horizontal = 20.dp, vertical = 18.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    Box(
                        modifier = Modifier.size(64.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Box(
                            modifier = Modifier
                                .size(64.dp)
                                .rotate(ringRotation)
                        ) {
                            Canvas(modifier = Modifier.fillMaxSize()) {
                                val strokeWidth = size.minDimension * 0.09f
                                val radius = size.minDimension / 2f - strokeWidth / 2f
                                drawCircle(
                                    color = AppPrimary.copy(alpha = 0.08f),
                                    radius = radius
                                )
                                drawArc(
                                    color = AppPrimary.copy(alpha = 0.92f),
                                    startAngle = 28f,
                                    sweepAngle = 128f,
                                    useCenter = false,
                                    style = Stroke(width = strokeWidth, cap = StrokeCap.Round)
                                )
                                drawArc(
                                    color = AppPurpleText.copy(alpha = 0.68f),
                                    startAngle = 210f,
                                    sweepAngle = 86f,
                                    useCenter = false,
                                    style = Stroke(width = strokeWidth * 0.82f, cap = StrokeCap.Round)
                                )
                            }
                        }

                        Box(
                            modifier = Modifier
                                .size(40.dp)
                                .scale(pulse)
                                .background(
                                    brush = Brush.radialGradient(
                                        colors = listOf(
                                            AppPrimary.copy(alpha = 0.18f),
                                            AppPrimarySoft.copy(alpha = 0.42f),
                                            Color.Transparent
                                        )
                                    ),
                                    shape = CircleShape
                                ),
                            contentAlignment = Alignment.Center
                        ) {
                            Box(
                                modifier = Modifier
                                    .size(32.dp)
                                    .background(
                                        brush = Brush.linearGradient(
                                            colors = listOf(
                                                MaterialTheme.colorScheme.surface,
                                                MaterialTheme.colorScheme.surface.copy(alpha = 0.96f)
                                            )
                                        ),
                                        shape = CircleShape
                                    ),
                                contentAlignment = Alignment.Center
                            ) {
                                androidx.compose.foundation.Image(
                                    painter = painterResource(Res.drawable.ic_launcher),
                                    contentDescription = null,
                                    modifier = Modifier
                                        .size(20.dp)
                                        .alpha(0.98f),
                                    contentScale = ContentScale.Fit
                                )
                            }
                        }

                        val orbitPhases = listOf(0f, 2.1f, 4.2f)
                        orbitPhases.forEachIndexed { index, phase ->
                            val angle = orbit * 2f * PI.toFloat() + phase
                            val x = (cos(angle) * 22f).dp
                            val y = (sin(angle) * 22f).dp
                            val dotColor = when (index) {
                                0 -> AppPrimary
                                1 -> AppPurpleText
                                else -> AppPrimary.copy(alpha = 0.9f)
                            }

                            Box(
                                modifier = Modifier
                                    .align(Alignment.Center)
                                    .offset(x = x, y = y)
                                    .size(5.dp)
                                    .background(color = dotColor, shape = CircleShape)
                            )
                        }
                    }

                    Spacer(modifier = Modifier.height(14.dp))

                    Text(
                        text = title,
                        style = MaterialTheme.typography.titleSmall,
                        color = AppNavy
                    )
                    Spacer(modifier = Modifier.height(4.dp))
                    Text(
                        text = subtitle,
                        style = MaterialTheme.typography.bodySmall,
                        color = AppMuted
                    )

                    Spacer(modifier = Modifier.height(12.dp))

                    Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                        repeat(3) { index ->
                            val phase = (orbit + index * 0.24f) % 1f
                            val dotAlpha = 0.35f + (0.65f * kotlin.math.abs(cos(phase * PI.toFloat())))
                            Box(
                                modifier = Modifier
                                    .size(6.dp)
                                    .background(
                                        color = AppPrimary.copy(alpha = dotAlpha),
                                        shape = CircleShape
                                    )
                            )
                        }
                    }
                }
            }
        }
    }
}
